/**
 * Streams chat completions from the Cloudflare Worker proxy `/chat`
 * route. Mirrors the request shape used by the native `ClaudeAPI.swift`
 * — the worker forwards directly to `api.anthropic.com/v1/messages`, so
 * we send Anthropic's native message format.
 *
 * Streaming is SSE: we parse `event:` / `data:` lines ourselves rather
 * than pull in an EventSource polyfill, because the browser `EventSource`
 * cannot POST a body.
 *
 * The stream parser handles two kinds of content blocks:
 *   - text         → text_delta chunks → onTextChunk callback
 *   - tool_use     → input_json_delta partials → onToolUse on stop
 *
 * RELIABILITY (the "koko randomly breaks" fixes — 2026-07-04 yap):
 *   1. Transient failures RETRY automatically (network errors, HTTP
 *      408/409/425/429/5xx/529, and Anthropic `error` SSE frames like
 *      `overloaded_error`) with jittered backoff — as long as nothing
 *      has been surfaced to the UI yet. Once text or a tool_use has
 *      been delivered we can't safely replay (side effects), so those
 *      failures surface through onError instead of silently retrying.
 *   2. Anthropic mid-stream `error` events are now DETECTED. They used
 *      to be silently ignored; the stream would end looking "complete"
 *      with half a response and dropped tool calls — the exact
 *      "breaks at a random point" symptom.
 *   3. A stall watchdog aborts the read when no bytes arrive for
 *      `STREAM_STALL_TIMEOUT_MS`, so a hung connection can never wedge
 *      the bird in "thinking" forever.
 *   4. tool_use blocks whose `content_block_stop` never arrived (the
 *      worker dropped the connection mid-block) are flushed at stream
 *      end with best-effort JSON repair instead of being dropped.
 *   5. Truncated tool-input JSON is repaired (unclosed strings /
 *      brackets) before we give up and fall back to `{}`.
 *
 * For history validity, any assistant message in the conversation that
 * contained tool_use blocks must be followed by a user message starting
 * with matching tool_result blocks. Studio tools are fire-and-forget
 * side effects — there's no real result to return — so we synthesize a
 * minimal `"ok"` tool_result for each pending id when building the
 * outgoing user message.
 */

import type {
  AnthropicContentBlock,
  AnthropicTool,
  ConversationMessage,
  PageContext,
  ToolUseBlock,
} from "../types";

const DEFAULT_MODEL = "claude-sonnet-4-6";
/**
 * Per-turn token budget. 1024 caused Claude to give up partway through
 * multi-tool requests; 4096 still truncated long "populate the whole
 * project" turns mid-tool-call (truncated JSON → dropped tool → the
 * bird "randomly stops"). 8192 gives full-pipeline turns comfortable
 * headroom without meaningfully changing latency on short turns.
 */
const DEFAULT_MAX_TOKENS = 8192;

/** Max attempts per turn (1 initial + up to 2 retries). */
const MAX_STREAM_ATTEMPTS = 3;
/** Base backoff between attempts; jitter of ±50% is applied. */
const RETRY_BACKOFF_BASE_MS = 500;
/**
 * Abort the read if no bytes arrive for this long. Anthropic emits
 * `ping` frames every few seconds, so a healthy stream never goes
 * quiet anywhere near this long — a hit means the connection is dead.
 */
const STREAM_STALL_TIMEOUT_MS = 45_000;

/** HTTP statuses worth retrying — transient by definition. */
const RETRYABLE_HTTP_STATUSES = new Set([408, 409, 425, 429, 500, 502, 503, 504, 529]);

export interface ClaudeStreamCallbacks {
  onTextChunk: (chunkText: string) => void;
  /**
   * Fired once per `tool_use` block as soon as its `content_block_stop`
   * event arrives. Side-effect handlers run client-side; the prototype
   * does not roundtrip a real tool_result payload back in the same turn.
   */
  onToolUse?: (block: ToolUseBlock) => void;
  /**
   * Final assistant-message text and any completed tool_use blocks.
   * Caller decides how to persist these into conversation history.
   */
  onComplete: (finalFullText: string, toolUses: ToolUseBlock[]) => void;
  onError: (error: Error) => void;
  /**
   * Optional: fired when a transient failure is being retried, so the
   * host can surface a subtle "reconnecting…" hint. attempt is the
   * 1-based attempt number that just failed.
   */
  onRetry?: (attempt: number, reason: string) => void;
}

export interface ClaudeStreamRequest {
  systemPrompt: string;
  history: readonly ConversationMessage[];
  userText: string;
  pageContext: PageContext;
  model?: string;
  /**
   * Whether to allow Claude to use Anthropic's server-side web search
   * tool. When true the request includes `web_search` in the `tools`
   * array; Claude decides per-turn whether to invoke it (so leaving
   * this on doesn't force a search on every turn — only the ones
   * that need fresh info).
   */
  webSearchEnabled?: boolean;
  /**
   * Custom tools defined by the host page (e.g. studio's add_character).
   * Merged into the same `tools` array as web_search; Claude picks
   * whichever (if any) per turn.
   */
  customTools?: readonly AnthropicTool[];
}

const WEB_SEARCH_MAX_USES_PER_TURN = 3;

/** Internal signal distinguishing retryable failures from fatal ones. */
class TransientStreamError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TransientStreamError";
  }
}

export class ClaudeClient {
  private readonly workerBaseUrl: string;
  private activeAbortController: AbortController | null = null;

  constructor(workerBaseUrl: string) {
    this.workerBaseUrl = workerBaseUrl.replace(/\/$/, "");
  }

  /** Cancels any in-flight stream. Safe to call from anywhere. */
  cancelInFlight(): void {
    if (this.activeAbortController) {
      this.activeAbortController.abort();
      this.activeAbortController = null;
    }
  }

  /**
   * Stream one conversation turn, retrying transient failures.
   *
   * Retry safety rule: a retry replays the WHOLE request, so we only
   * auto-retry while nothing has been emitted to the callbacks yet.
   * After the first text chunk / tool_use is delivered, side effects
   * may have run (bird choreography, streamed bubble text) and a
   * replay would double them — those failures surface via onError,
   * and the host's failTurn path keeps the UI un-wedged.
   */
  async streamResponse(
    request: ClaudeStreamRequest,
    callbacks: ClaudeStreamCallbacks,
  ): Promise<void> {
    this.cancelInFlight();
    const abortController = new AbortController();
    this.activeAbortController = abortController;

    try {
      for (let attempt = 1; attempt <= MAX_STREAM_ATTEMPTS; attempt++) {
        if (abortController.signal.aborted) return;
        const emissionTracker = { emitted: false };
        try {
          await this.streamOnce(request, callbacks, abortController, emissionTracker);
          return; // clean completion
        } catch (error) {
          if (abortController.signal.aborted) return;
          const isTransient = error instanceof TransientStreamError;
          const canRetry =
            isTransient && !emissionTracker.emitted && attempt < MAX_STREAM_ATTEMPTS;
          if (!canRetry) {
            callbacks.onError(
              error instanceof Error ? error : new Error(String(error)),
            );
            return;
          }
          const reason = (error as Error).message;
          console.warn(
            `[koko] transient /chat failure (attempt ${attempt}/${MAX_STREAM_ATTEMPTS}), retrying: ${reason}`,
          );
          callbacks.onRetry?.(attempt, reason);
          await backoffDelay(attempt, abortController.signal);
        }
      }
    } finally {
      if (this.activeAbortController === abortController) {
        this.activeAbortController = null;
      }
    }
  }

  /**
   * One attempt: POST, read SSE frames, dispatch callbacks. Throws
   * `TransientStreamError` for failures the caller may retry, plain
   * `Error` for fatal ones. Resolves after calling onComplete.
   */
  private async streamOnce(
    request: ClaudeStreamRequest,
    callbacks: ClaudeStreamCallbacks,
    abortController: AbortController,
    emissionTracker: { emitted: boolean },
  ): Promise<void> {
    const messages = buildRequestMessages(
      request.history,
      request.userText,
      request.pageContext,
    );

    const tools: unknown[] = [];
    if (request.webSearchEnabled) {
      tools.push({
        type: "web_search_20250305",
        name: "web_search",
        max_uses: WEB_SEARCH_MAX_USES_PER_TURN,
      });
    }
    if (request.customTools && request.customTools.length > 0) {
      tools.push(...request.customTools);
    }

    const body: Record<string, unknown> = {
      model: request.model ?? DEFAULT_MODEL,
      max_tokens: DEFAULT_MAX_TOKENS,
      stream: true,
      system: request.systemPrompt,
      messages,
    };

    if (tools.length > 0) {
      body.tools = tools;
    }

    let response: Response;
    try {
      response = await fetch(`${this.workerBaseUrl}/chat`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
        signal: abortController.signal,
      });
    } catch (error) {
      if (abortController.signal.aborted) throw error;
      // Network-level failures are transient by nature.
      throw new TransientStreamError(
        `Network error calling /chat: ${(error as Error).message}`,
      );
    }

    if (!response.ok || !response.body) {
      const errorText = await response.text().catch(() => "");
      const message = `/chat returned ${response.status}: ${errorText.slice(0, 400)}`;
      if (RETRYABLE_HTTP_STATUSES.has(response.status) || !response.body) {
        throw new TransientStreamError(message);
      }
      throw new Error(message);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let sseBuffer = "";
    let accumulatedText = "";
    /** Set when an Anthropic `error` SSE frame arrives mid-stream. */
    let streamErrorFrame: { type: string; message: string } | null = null;
    /**
     * Per-content-block partial state. Keyed by Anthropic's `index`
     * field (an integer assigned per block in the response). We only
     * track tool_use blocks here; text blocks just accumulate into
     * `accumulatedText`.
     */
    const inFlightToolUseBlocks = new Map<
      number,
      { id: string; name: string; partialJson: string }
    >();
    const completedToolUseBlocks: ToolUseBlock[] = [];
    let stopReason: string | null = null;

    const frameHandlers: FrameHandlers = {
      onText: (chunk) => {
        accumulatedText += chunk;
        emissionTracker.emitted = true;
        callbacks.onTextChunk(chunk);
      },
      onToolUseStart: (index, id, name) => {
        inFlightToolUseBlocks.set(index, { id, name, partialJson: "" });
      },
      onToolUseInputDelta: (index, partial) => {
        const block = inFlightToolUseBlocks.get(index);
        if (block) block.partialJson += partial;
      },
      onContentBlockStop: (index) => {
        const block = inFlightToolUseBlocks.get(index);
        if (!block) return;
        inFlightToolUseBlocks.delete(index);
        const parsedInput = parseToolInputJson(block.partialJson);
        const completed: ToolUseBlock = {
          id: block.id,
          name: block.name,
          input: parsedInput,
        };
        completedToolUseBlocks.push(completed);
        emissionTracker.emitted = true;
        try {
          callbacks.onToolUse?.(completed);
        } catch (error) {
          // A throwing tool handler must not kill the stream — the
          // remaining blocks/text of this turn still matter.
          console.warn(
            `[koko] onToolUse handler threw for ${completed.name}:`,
            error,
          );
        }
      },
      onStreamError: (errorType, message) => {
        streamErrorFrame = { type: errorType, message };
      },
      onStopReason: (reason) => {
        stopReason = reason;
      },
    };

    try {
      while (true) {
        const { value, done } = await readWithStallTimeout(reader);
        if (done) break;
        sseBuffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by blank lines.
        let frameBoundary = sseBuffer.indexOf("\n\n");
        while (frameBoundary !== -1) {
          const frame = sseBuffer.slice(0, frameBoundary);
          sseBuffer = sseBuffer.slice(frameBoundary + 2);
          processFrame(frame, frameHandlers);
          frameBoundary = sseBuffer.indexOf("\n\n");
        }
        if (streamErrorFrame) break; // upstream said it's over — stop reading
      }
    } catch (error) {
      if (abortController.signal.aborted) throw error;
      throw new TransientStreamError(
        `Error reading SSE stream: ${(error as Error).message}`,
      );
    } finally {
      // Release the connection on every exit path (incl. error frames).
      try {
        reader.releaseLock();
      } catch {
        // Already released — fine.
      }
    }

    if (streamErrorFrame !== null) {
      const frame = streamErrorFrame as { type: string; message: string };
      const message = `Claude stream error (${frame.type}): ${frame.message}`;
      // overloaded / api errors are transient; invalid_request is not.
      if (frame.type === "invalid_request_error") {
        throw new Error(message);
      }
      throw new TransientStreamError(message);
    }

    // Flush any tool_use block whose content_block_stop never arrived
    // (connection cut mid-block). Best-effort repair beats silently
    // dropping the user's requested action on the floor.
    for (const [index, block] of inFlightToolUseBlocks) {
      inFlightToolUseBlocks.delete(index);
      const repairedInput = parseToolInputJson(block.partialJson);
      const flushed: ToolUseBlock = {
        id: block.id,
        name: block.name,
        input: repairedInput,
      };
      console.warn(
        `[koko] flushing unterminated tool_use block ${block.name} (stream ended early)`,
      );
      completedToolUseBlocks.push(flushed);
      emissionTracker.emitted = true;
      try {
        callbacks.onToolUse?.(flushed);
      } catch (error) {
        console.warn(`[koko] onToolUse handler threw for ${flushed.name}:`, error);
      }
    }

    if (stopReason === "max_tokens") {
      console.warn(
        "[koko] turn hit max_tokens — response may be truncated. Consider splitting the request.",
      );
    }

    callbacks.onComplete(accumulatedText, completedToolUseBlocks);
  }
}

/**
 * `reader.read()` guarded by the stall watchdog: if no bytes arrive
 * within `STREAM_STALL_TIMEOUT_MS`, cancel the reader and throw so the
 * attempt fails fast instead of hanging the bird in "thinking" forever.
 */
async function readWithStallTimeout(
  reader: ReadableStreamDefaultReader<Uint8Array>,
): Promise<ReadableStreamReadResult<Uint8Array>> {
  let stallTimerId: number | undefined;
  const stallPromise = new Promise<never>((_, reject) => {
    stallTimerId = window.setTimeout(() => {
      // Cancel the underlying stream so the pending read settles.
      void reader.cancel().catch(() => undefined);
      reject(
        new Error(
          `no data for ${Math.round(STREAM_STALL_TIMEOUT_MS / 1000)}s (stalled stream)`,
        ),
      );
    }, STREAM_STALL_TIMEOUT_MS);
  });
  try {
    return await Promise.race([reader.read(), stallPromise]);
  } finally {
    window.clearTimeout(stallTimerId);
  }
}

/** Jittered exponential backoff, cancellable via the abort signal. */
function backoffDelay(attempt: number, signal: AbortSignal): Promise<void> {
  const base = RETRY_BACKOFF_BASE_MS * Math.pow(3, attempt - 1);
  const jitter = base * (0.5 + Math.random()); // 0.5x–1.5x
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, jitter);
    const onAbort = () => {
      window.clearTimeout(timer);
      resolve();
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Build the `messages` array for the outgoing request.
 *
 * Two history-validity rules drive the shape:
 *   1. Any user message that follows an assistant message with
 *      `tool_use` blocks must START with a `tool_result` block for
 *      every such id. Anthropic 400s otherwise.
 *   2. The new user turn (the one being added right now) is no
 *      different — if the most recent assistant message had tool_uses,
 *      the new user message gets the same tool_result preface.
 *
 * Studio tools are fire-and-forget side effects, so the result content
 * is just a no-op `"ok"` string — Claude only needs the `tool_use_id`
 * to satisfy the API contract.
 */
export function buildRequestMessages(
  history: readonly ConversationMessage[],
  newUserText: string,
  pageContext: PageContext,
): unknown[] {
  const out: unknown[] = [];

  for (let messageIndex = 0; messageIndex < history.length; messageIndex++) {
    const message = history[messageIndex];

    if (message.role === "assistant") {
      out.push({ role: "assistant", content: message.content });
      // HARDENING (live 400, 2026-07-06): if this assistant turn holds
      // tool_use blocks but the next history entry is NOT a user
      // message (another assistant append slipped in, e.g. an older
      // report-back), inject a synthetic user acknowledgment so the
      // API's tool_use→tool_result pairing always holds no matter what
      // any append path did to the history.
      const toolUseIds = extractToolUseIds(message);
      const next = history[messageIndex + 1];
      if (toolUseIds.length > 0 && next && next.role !== "user") {
        out.push({
          role: "user",
          content: toolUseIds.map((toolUseId) => ({
            type: "tool_result",
            tool_use_id: toolUseId,
            content: "ok",
          })),
        });
      }
      continue;
    }

    // User message — possibly needs tool_result preface based on the
    // assistant message immediately before it.
    const previousMessage = history[messageIndex - 1];
    const toolUseIdsToAcknowledge = extractToolUseIds(previousMessage);

    if (toolUseIdsToAcknowledge.length === 0) {
      out.push({ role: "user", content: message.content });
      continue;
    }

    const toolResultBlocks: AnthropicContentBlock[] = toolUseIdsToAcknowledge.map(
      (toolUseId) => ({
        type: "tool_result",
        tool_use_id: toolUseId,
        content: "ok",
      }),
    );
    const baseUserContent: AnthropicContentBlock[] =
      typeof message.content === "string"
        ? [{ type: "text", text: message.content }]
        : message.content;
    out.push({
      role: "user",
      content: [...toolResultBlocks, ...baseUserContent],
    });
  }

  // The new user turn being appended right now.
  const lastHistoryMessage = history[history.length - 1];
  const pendingToolUseIds = extractToolUseIds(lastHistoryMessage);
  const newUserContent: AnthropicContentBlock[] = [
    ...pendingToolUseIds.map((toolUseId) => ({
      type: "tool_result" as const,
      tool_use_id: toolUseId,
      content: "ok",
    })),
    {
      type: "image",
      source: {
        type: "base64",
        media_type: pageContext.screenshotMediaType,
        data: pageContext.screenshotBase64,
      },
    },
    {
      type: "text",
      text:
        `Viewport screenshot (${pageContext.viewportWidth}x${pageContext.viewportHeight} pixels).\n\n` +
        `Page summary:\n${pageContext.domSummary}\n\n` +
        `User said:\n${newUserText}`,
    },
  ];
  out.push({ role: "user", content: newUserContent });

  return out;
}

/**
 * Pull tool_use ids out of an assistant message's content array, if
 * any. Returns an empty array for plain-text messages or when the
 * argument is absent.
 */
function extractToolUseIds(
  message: ConversationMessage | undefined,
): string[] {
  if (!message || message.role !== "assistant") return [];
  if (!Array.isArray(message.content)) return [];
  const ids: string[] = [];
  for (const block of message.content) {
    if (
      block &&
      typeof block === "object" &&
      "type" in block &&
      block.type === "tool_use" &&
      "id" in block &&
      typeof block.id === "string"
    ) {
      ids.push(block.id);
    }
  }
  return ids;
}

/**
 * Parse the accumulated `input_json_delta` payload for a single
 * tool_use block. Anthropic streams a STRING that is intended to be
 * concatenated and parsed as one JSON value — empty string means the
 * tool was called with no arguments (input is `{}`).
 *
 * When the payload is TRUNCATED (max_tokens hit or connection dropped
 * mid-block), a strict parse fails and the tool used to be silently
 * dropped. We now attempt a structural repair — close any unterminated
 * string and any unclosed brackets — so a mostly-complete call (e.g.
 * a long description missing its last few words) still dispatches.
 */
export function parseToolInputJson(accumulatedPartialJson: string): unknown {
  const trimmed = accumulatedPartialJson.trim();
  if (trimmed.length === 0) return {};
  try {
    return JSON.parse(trimmed);
  } catch {
    const repaired = repairTruncatedJson(trimmed);
    if (repaired !== null) {
      try {
        const parsed = JSON.parse(repaired);
        console.warn(
          "[koko] repaired truncated tool_use input JSON:",
          trimmed.slice(0, 120),
        );
        return parsed;
      } catch {
        // fall through
      }
    }
    console.warn(
      "[koko] failed to parse tool_use input_json (using empty input):",
      trimmed.slice(0, 200),
    );
    return {};
  }
}

/**
 * Structural repair for truncated JSON: walks the string tracking
 * string/escape state and an open-bracket stack, trims a dangling
 * escape or partial token, then appends the missing closers. Returns
 * null when the input doesn't even start like a JSON object/array.
 */
export function repairTruncatedJson(truncated: string): string | null {
  if (!truncated.startsWith("{") && !truncated.startsWith("[")) return null;
  const bracketStack: string[] = [];
  let inString = false;
  let isEscaped = false;
  for (const character of truncated) {
    if (inString) {
      if (isEscaped) {
        isEscaped = false;
      } else if (character === "\\") {
        isEscaped = true;
      } else if (character === '"') {
        inString = false;
      }
      continue;
    }
    if (character === '"') {
      inString = true;
    } else if (character === "{" || character === "[") {
      bracketStack.push(character);
    } else if (character === "}" || character === "]") {
      bracketStack.pop();
    }
  }

  let repaired = truncated;
  // A dangling backslash can't be closed meaningfully — drop it.
  if (inString && isEscaped) repaired = repaired.slice(0, -1);
  if (inString) repaired += '"';
  // Trim a trailing comma / colon that would invalidate the close.
  repaired = repaired.replace(/[,:]\s*$/, "");
  // If a key was left without a value (`"key":`), the regex above
  // removed the colon, leaving a bare string key — that's still valid
  // to close as long as brackets match... it isn't valid JSON for an
  // object, so drop the dangling key entirely.
  repaired = repaired.replace(/,?\s*"[^"]*"\s*$/, (match, offset: number) => {
    // Only drop when we're inside an object (not an array of strings).
    const lastOpen = bracketStack[bracketStack.length - 1];
    if (lastOpen === "{") {
      // Check the char before the match isn't a colon (then it's a value).
      const before = repaired.slice(0, offset).replace(/\s+$/, "");
      if (!before.endsWith(":")) return "";
    }
    return match;
  });
  for (let i = bracketStack.length - 1; i >= 0; i--) {
    repaired += bracketStack[i] === "{" ? "}" : "]";
  }
  return repaired;
}

interface FrameHandlers {
  onText: (chunk: string) => void;
  onToolUseStart: (index: number, id: string, name: string) => void;
  onToolUseInputDelta: (index: number, partial: string) => void;
  onContentBlockStop: (index: number) => void;
  /** Anthropic `error` event (overloaded_error, api_error, …). */
  onStreamError: (errorType: string, message: string) => void;
  /** stop_reason from message_delta (e.g. "max_tokens", "end_turn"). */
  onStopReason: (reason: string) => void;
}

/**
 * Process a single SSE frame. Frame may have multiple `data:` lines but
 * Anthropic always uses one. We dispatch on the parsed JSON `type`
 * field to drive both text streaming and tool_use accumulation.
 */
function processFrame(frame: string, handlers: FrameHandlers): void {
  const lines = frame.split("\n");
  for (const line of lines) {
    if (!line.startsWith("data:")) continue;
    const payload = line.slice(5).trim();
    if (payload.length === 0 || payload === "[DONE]") continue;

    let parsed: {
      type?: string;
      index?: number;
      content_block?: {
        type?: string;
        id?: string;
        name?: string;
      };
      delta?: {
        type?: string;
        text?: string;
        partial_json?: string;
        stop_reason?: string;
      };
      error?: {
        type?: string;
        message?: string;
      };
    };
    try {
      parsed = JSON.parse(payload);
    } catch {
      // Malformed frame; ignore and keep streaming.
      continue;
    }

    if (parsed.type === "error") {
      handlers.onStreamError(
        parsed.error?.type ?? "unknown_error",
        parsed.error?.message ?? "upstream stream error",
      );
      continue;
    }

    if (parsed.type === "content_block_start") {
      const blockIndex = parsed.index;
      const block = parsed.content_block;
      if (
        typeof blockIndex === "number" &&
        block?.type === "tool_use" &&
        typeof block.id === "string" &&
        typeof block.name === "string"
      ) {
        handlers.onToolUseStart(blockIndex, block.id, block.name);
      }
      continue;
    }

    if (parsed.type === "content_block_delta") {
      const blockIndex = parsed.index;
      const delta = parsed.delta;
      if (delta?.type === "text_delta" && typeof delta.text === "string") {
        handlers.onText(delta.text);
      } else if (
        delta?.type === "input_json_delta" &&
        typeof delta.partial_json === "string" &&
        typeof blockIndex === "number"
      ) {
        handlers.onToolUseInputDelta(blockIndex, delta.partial_json);
      }
      continue;
    }

    if (parsed.type === "message_delta") {
      if (typeof parsed.delta?.stop_reason === "string") {
        handlers.onStopReason(parsed.delta.stop_reason);
      }
      continue;
    }

    if (parsed.type === "content_block_stop") {
      if (typeof parsed.index === "number") {
        handlers.onContentBlockStop(parsed.index);
      }
      continue;
    }
  }
}
